import { useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import type { PackOpenCard } from '../../api/userCardApi';
import { apiGetCard } from '../../api/cardApi';
import CardDetailModal from './CardDetailModal';
import type { Card } from '../../types/models/card';
import { useAppSelector } from '../../store/hooks';
import useSwipeGesture from '../../components/Hooks/useSwipeGesture';

const storageUrl = process.env.REACT_APP_STORAGE_URL ?? '';
const toImgSrc = (path: string) =>
  path.startsWith('http') ? path : storageUrl + path;

// ─── Shared ───────────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.88);
  overflow-y: auto;
  z-index: 1000;
`;

const OverlayInner = styled.div`
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  box-sizing: border-box;
`;

const CardFaceBase = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  border-radius: 10px;
  overflow: hidden;
`;

const CardBack = styled(CardFaceBase)`
  background-color: #1a1a1a;
  border: 2px solid rgba(255, 215, 0, 0.3);
`;

const CardBackImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  background-color: #111;
`;

const CardBackPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: rgba(255, 215, 0, 0.4);
  font-weight: 700;
  letter-spacing: 1px;
`;

const CardFront = styled(CardFaceBase)`
  transform: rotateY(180deg);
  background-color: #222;
`;

const CardImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  background-color: #111;
`;

const CardImgPlaceholder = styled.div`
  position: absolute;
  inset: 0;
  background-color: #2a2a2a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.15);
`;

const NewBadge = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background-color: gold;
  color: #000;
  font-size: 9px;
  font-weight: 700;
  padding: 3px 7px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 1;
`;

const DupeBadge = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background-color: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.6);
  font-size: 9px;
  font-weight: 700;
  padding: 3px 7px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 1;
`;

// ─── Desktop ──────────────────────────────────────────────────────────────────

const ModalTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: gold;
  margin: 0 0 6px;
  text-align: center;
`;

const CreditsLine = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0 0 28px;
  text-align: center;
`;

const CardsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  justify-content: center;
  max-width: 1100px;
  width: 100%;
  margin-bottom: 28px;
`;

const DesktopFlipContainer = styled.div<{ $flipped: boolean }>`
  width: 260px;
  aspect-ratio: 2 / 3;
  perspective: 800px;
  cursor: ${({ $flipped }) => ($flipped ? 'zoom-in' : 'pointer')};
  flex-shrink: 0;
`;

const FlipInner = styled.div<{ $flipped: boolean }>`
  width: 100%;
  height: 100%;
  position: relative;
  transform-style: preserve-3d;
  transition: transform 0.5s ease;
  transform: ${({ $flipped }) => ($flipped ? 'rotateY(180deg)' : 'rotateY(0deg)')};
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
`;

const RevealAllBtn = styled.button`
  padding: 10px 24px;
  background-color: rgba(255, 215, 0, 0.12);
  border: 1px solid gold;
  border-radius: 6px;
  color: gold;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`;

const CloseBtn = styled.button`
  padding: 10px 24px;
  background-color: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  cursor: pointer;
`;

// ─── Mobile single-card ───────────────────────────────────────────────────────

const MobileOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: radial-gradient(ellipse at center, #1a1a2e 0%, #0d0d0d 100%);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 20px 16px 28px;
  box-sizing: border-box;
  user-select: none;
`;

const MobileTopBar = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MobileCounter = styled.span`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.45);
  font-weight: 600;
`;

const MobileSkipBtn = styled.button`
  padding: 6px 14px;
  background: transparent;
  border: 1px solid rgba(255, 215, 0, 0.35);
  border-radius: 20px;
  color: rgba(255, 215, 0, 0.75);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

const MobileDotsRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const MobileDot = styled.div<{ $active: boolean; $done: boolean }>`
  width: ${({ $active }) => ($active ? '10px' : '7px')};
  height: ${({ $active }) => ($active ? '10px' : '7px')};
  border-radius: 50%;
  background-color: ${({ $active, $done }) =>
    $active ? 'gold' : $done ? 'rgba(255,215,0,0.45)' : 'rgba(255,255,255,0.15)'};
  transition: all 0.2s ease;
`;

const MobileCardArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 12px;
`;

const pulseHint = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.8; }
`;

const MobileHint = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
  margin: 0;
  animation: ${pulseHint} 2s ease-in-out infinite;
`;

const MobileFlipContainer = styled.div<{ $flipped: boolean }>`
  width: min(88vw, 380px);
  aspect-ratio: 2 / 3;
  perspective: 1000px;
  cursor: ${({ $flipped }) => ($flipped ? 'default' : 'pointer')};
  flex-shrink: 0;
`;

const MobileFlipInner = styled.div<{ $flipped: boolean }>`
  width: 100%;
  height: 100%;
  position: relative;
  transform-style: preserve-3d;
  transition: transform 0.55s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${({ $flipped }) => ($flipped ? 'rotateY(180deg)' : 'rotateY(0deg)')};
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const MobileBottomArea = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  min-height: 52px;
`;

const MobileNextBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 13px 36px;
  background: rgba(255, 215, 0, 0.1);
  border: 1px solid gold;
  border-radius: 28px;
  color: gold;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  animation: ${fadeIn} 0.3s ease;
`;

const MobileSwipeHint = styled.p`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.25);
  margin: 0;
  text-align: center;
`;

// ─── Mobile summary ───────────────────────────────────────────────────────────

const SummaryOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.92);
  overflow-y: auto;
  z-index: 1000;
`;

const SummaryInner = styled.div`
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px 16px 32px;
  box-sizing: border-box;
  gap: 20px;
`;

const SummaryTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: gold;
  margin: 0;
  text-align: center;
`;

const SummaryCredits = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.45);
  margin: 0;
  text-align: center;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  width: 100%;
  max-width: 360px;
`;

const SummaryCardWrapper = styled.div`
  position: relative;
  aspect-ratio: 2 / 3;
  border-radius: 8px;
  overflow: hidden;
  cursor: zoom-in;
  background: #1a1a1a;
  border: 1px solid rgba(255, 215, 0, 0.15);
`;

const SummaryCardImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  background: #111;
`;

const SummaryCardPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.15);
`;

const SummaryCloseBtn = styled.button`
  padding: 12px 40px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 15px;
  cursor: pointer;
  font-weight: 600;
`;

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  cards: PackOpenCard[];
  alreadyOwned: number[];
  creditsRemaining: number;
  onClose: () => void;
}

const PackRevealModal: React.FC<Props> = ({ cards, alreadyOwned, creditsRemaining, onClose }) => {
  const isMobile = useAppSelector((state) => state.responsive.isMobile);
  const isAuthenticated = useAppSelector(
    (state) => state.auth.isAuthenticated || state.auth.userLoggedState !== null
  );

  const [flipped, setFlipped] = useState<boolean[]>(cards.map(() => false));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showSummary, setShowSummary] = useState(false);
  const [detailCard, setDetailCard] = useState<Card | null>(null);
  const [loadingDetail, setLoadingDetail] = useState<number | null>(null);
  const [cardCache, setCardCache] = useState<Record<number, Card>>({});

  const allFlipped = flipped.every(Boolean);

  const flip = (i: number) => {
    if (flipped[i]) return;
    setFlipped((prev) => prev.map((v, idx) => (idx === i ? true : v)));
  };

  const goNext = useCallback(() => {
    if (currentIndex < cards.length - 1) {
      setCurrentIndex((i) => i + 1);
    } else {
      setShowSummary(true);
    }
  }, [currentIndex, cards.length]);

  const revealAll = () => {
    setFlipped(cards.map(() => true));
    if (isMobile) setShowSummary(true);
  };

  const openDetail = async (card: PackOpenCard) => {
    if (loadingDetail === card.id) return;
    if (cardCache[card.id]) {
      setDetailCard(cardCache[card.id]);
      return;
    }
    setLoadingDetail(card.id);
    try {
      const full = await apiGetCard(card.id);
      setCardCache((prev) => ({ ...prev, [card.id]: full }));
      setDetailCard(full);
    } catch {
      // noop
    } finally {
      setLoadingDetail(null);
    }
  };

  const currentCard = cards[currentIndex];
  const currentFlipped = flipped[currentIndex];
  const isCurrentNew = currentCard ? !alreadyOwned.includes(currentCard.id) : false;
  const isLastCard = currentIndex === cards.length - 1;

  const swipeHandlers = useSwipeGesture(
    () => { if (currentFlipped) goNext(); },
    () => {},
    60
  );

  // ── Mobile summary screen ─────────────────────────────────────────────────
  if (isMobile && showSummary) {
    return (
      <>
        <SummaryOverlay>
          <SummaryInner>
            <SummaryTitle>Pacchetto aperto!</SummaryTitle>
            <SummaryCredits>Crediti rimanenti: {creditsRemaining}</SummaryCredits>

            <SummaryGrid>
              {cards.map((card, i) => {
                const isNew = !alreadyOwned.includes(card.id);
                const src = card.fronte_path ? toImgSrc(card.fronte_path) : null;
                return (
                  <SummaryCardWrapper
                    key={i}
                    onClick={() => openDetail(card)}
                    style={{ cursor: loadingDetail === card.id ? 'wait' : 'zoom-in' }}
                  >
                    {isNew
                      ? <NewBadge>Nuova!</NewBadge>
                      : <DupeBadge>Già posseduta</DupeBadge>
                    }
                    {src
                      ? <SummaryCardImg src={src} alt={`Carta #${card.id}`} />
                      : <SummaryCardPlaceholder>#{card.id}</SummaryCardPlaceholder>
                    }
                  </SummaryCardWrapper>
                );
              })}
            </SummaryGrid>

            <SummaryCloseBtn onClick={onClose}>Chiudi</SummaryCloseBtn>
          </SummaryInner>
        </SummaryOverlay>

        {detailCard && (
          <CardDetailModal
            card={detailCard}
            ownedQuantity={alreadyOwned.includes(detailCard.id) ? 2 : 1}
            isAuthenticated={isAuthenticated}
            onClose={() => setDetailCard(null)}
          />
        )}
      </>
    );
  }

  // ── Mobile single-card view ───────────────────────────────────────────────
  if (isMobile && currentCard) {
    const src = currentCard.fronte_path ? toImgSrc(currentCard.fronte_path) : null;

    return (
      <>
        <MobileOverlay {...swipeHandlers}>
          <MobileTopBar>
            <MobileCounter>{currentIndex + 1} / {cards.length}</MobileCounter>
            <MobileDotsRow>
              {cards.map((_, i) => (
                <MobileDot
                  key={i}
                  $active={i === currentIndex}
                  $done={i < currentIndex || flipped[i]}
                />
              ))}
            </MobileDotsRow>
            <MobileSkipBtn onClick={revealAll}>Rivela tutte</MobileSkipBtn>
          </MobileTopBar>

          <MobileCardArea>
            {!currentFlipped && <MobileHint>Tocca la carta per girarla</MobileHint>}
            {currentFlipped && <MobileHint style={{ animation: 'none', opacity: 0 }}>‎</MobileHint>}

            <MobileFlipContainer
              $flipped={currentFlipped}
              onClick={() => { if (!currentFlipped) flip(currentIndex); else goNext(); }}
            >
              <MobileFlipInner $flipped={currentFlipped}>
                <CardBack>
                  {currentCard.retro_path
                    ? <CardBackImg src={toImgSrc(currentCard.retro_path)} alt="retro" />
                    : <CardBackPlaceholder>GWF</CardBackPlaceholder>
                  }
                </CardBack>
                <CardFront>
                  {currentFlipped && (
                    <>
                      {isCurrentNew
                        ? <NewBadge>Nuova!</NewBadge>
                        : <DupeBadge>Già posseduta</DupeBadge>
                      }
                      {src
                        ? <CardImg src={src} alt={`Carta #${currentCard.id}`} />
                        : <CardImgPlaceholder>#{currentCard.id}</CardImgPlaceholder>
                      }
                    </>
                  )}
                </CardFront>
              </MobileFlipInner>
            </MobileFlipContainer>
          </MobileCardArea>

          <MobileBottomArea>
            {currentFlipped && (
              <>
                <MobileNextBtn onClick={goNext}>
                  {isLastCard ? 'Vedi riepilogo' : 'Prossima'} →
                </MobileNextBtn>
                {!isLastCard && <MobileSwipeHint>oppure scorri a sinistra</MobileSwipeHint>}
              </>
            )}
          </MobileBottomArea>
        </MobileOverlay>

        {detailCard && (
          <CardDetailModal
            card={detailCard}
            ownedQuantity={alreadyOwned.includes(detailCard.id) ? 2 : 1}
            isAuthenticated={isAuthenticated}
            onClose={() => setDetailCard(null)}
          />
        )}
      </>
    );
  }

  // ── Desktop view (unchanged) ──────────────────────────────────────────────
  return (
    <>
      <Overlay onClick={(e) => e.target === e.currentTarget && allFlipped && onClose()}>
        <OverlayInner>
          <ModalTitle>Pacchetto aperto!</ModalTitle>
          <CreditsLine>Crediti rimanenti: {creditsRemaining}</CreditsLine>

          <CardsRow>
            {cards.map((card, i) => {
              const isNew = !alreadyOwned.includes(card.id);
              const src = card.fronte_path ? toImgSrc(card.fronte_path) : null;
              const isLoadingThis = loadingDetail === card.id;
              return (
                <DesktopFlipContainer
                  key={i}
                  $flipped={flipped[i]}
                  onClick={() => (flipped[i] ? openDetail(card) : flip(i))}
                  style={flipped[i] ? { cursor: isLoadingThis ? 'wait' : 'zoom-in' } : undefined}
                >
                  <FlipInner $flipped={flipped[i]}>
                    <CardBack>
                      {card.retro_path
                        ? <CardBackImg src={toImgSrc(card.retro_path)} alt="retro" />
                        : <CardBackPlaceholder>GWF</CardBackPlaceholder>
                      }
                    </CardBack>
                    <CardFront>
                      {flipped[i] && (isNew
                        ? <NewBadge>Nuova!</NewBadge>
                        : <DupeBadge>Già posseduta</DupeBadge>
                      )}
                      {src
                        ? <CardImg src={src} alt={`Carta #${card.id}`} />
                        : <CardImgPlaceholder>#{card.id}</CardImgPlaceholder>
                      }
                    </CardFront>
                  </FlipInner>
                </DesktopFlipContainer>
              );
            })}
          </CardsRow>

          <ActionsRow>
            {!allFlipped && <RevealAllBtn onClick={revealAll}>Rivela tutte</RevealAllBtn>}
            {allFlipped && <CloseBtn onClick={onClose}>Chiudi</CloseBtn>}
          </ActionsRow>
        </OverlayInner>
      </Overlay>

      {detailCard && (
        <CardDetailModal
          card={detailCard}
          ownedQuantity={alreadyOwned.includes(detailCard.id) ? 2 : 1}
          isAuthenticated={isAuthenticated}
          onClose={() => setDetailCard(null)}
        />
      )}
    </>
  );
};

export default PackRevealModal;
